import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import ReactModal from 'react-modal'
import TabIndicator from '../Tabs/TabIndicator'
import IconComponent from '../Icon/Icon'
import { matchPath } from 'react-router'

const color = PropTypes.color
const Icon = PropTypes.Icon
const HORIZ_ALIGN = ['center', 'start', 'end']
const TRIGGERS = ['hover', 'divider', 'button']
const LAYOUT = ['navbar', 'sidebar']
const POSITIONS = ['left', 'right', 'top', 'bottom']
const ICON_SIZE = ['lg', '2x', '3x', '4x', '5x']

const theme = {
  navBackground: color,
  navColor: color,
  navHoverColor: color,
  navActiveColor: color,
  navActiveIndicatorColor: color,
  shadow: PropTypes.string,
  rightBorder: PropTypes.string,
  toggleButtonColor: color
}

const sideBarSettings = {
  /**
   * Sidebar toggle trigger
   */
  sidebarToggleTrigger: PropTypes.oneOf(TRIGGERS),
  /**
   * Sidebar toggle trigger
   */
  triggerIcon: Icon,
  /**
   * Determines if sidebar is expanded initially
   */
  isSidebarExpanded: PropTypes.bool,
  /**
   * Determines if sidebar is expandable
   */
  isSidebarExpandable: PropTypes.bool,
  /**
   * Determines if sidebar collapses on route changed
   */
  autoCollapse: PropTypes.bool
}

const navBarSettings = {
  /**
   * Show labels
   */
  showLabels: PropTypes.bool,

  /**
   * Alignment of items in Navigation (horizontal layout). Left, center, or right.
   */
  horizontalAlign: PropTypes.oneOf(HORIZ_ALIGN)
}

const RouteType = {
  href: PropTypes.string,
  route: PropTypes.string,
  label: PropTypes.string,
  icon: Icon,
  externalRoute: PropTypes.bool,
} 

class Navigation extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape(RouteType)),

    /**
     * Allows user to determine layout between header navigation or sidebar.
     */
    navLayout: PropTypes.oneOf(LAYOUT),

    /**
     * Label position
     */
    labelPosition: PropTypes.oneOf(POSITIONS),

    useAsMobileNav: PropTypes.bool,

    showIcons: PropTypes.bool,

    /**
     * Sidebar settings
     */
    sidebarSettings: PropTypes.shape(sideBarSettings),

    /**
     * NavbarSettings
     */
    navbarSettings: PropTypes.shape(navBarSettings),

    mobileNavSettings: PropTypes.shape({
      icon: Icon,
      iconSize: PropTypes.oneOf(ICON_SIZE),
      iconColor: color,
      width: PropTypes.string,
      top: PropTypes.number,
      left: PropTypes.number,
      bottom: PropTypes.number,
      right: PropTypes.number,
      direction: PropTypes.oneOf(['X', 'Y']),
      hasOverlay: PropTypes.bool,
      hideCloseIcon: PropTypes.bool,
      closeIcon: Icon,
      closeIconSize: PropTypes.oneOf(ICON_SIZE),
      closeIconColor: color
    }),

    /**
     * Color settings for navigation.
     */
    styling: PropTypes.shape(theme),

    /**
     * Location object
     */
    location: PropTypes.object.isRequired
  }
  static defaultProps = {
    navLayout: 'sidebar',
    labelPosition: 'bottom',
    showIcons: true,
    styling: {
      navBackground: '#007bff',
      navColor: '#fbfbfb',
      navActiveColor: '#fff',
      navHoverColor: '#f1f1f1',
      navActiveIndicatorColor: '#fff',
      shadow: '1px 1px 6px grey',
      rightBorder: '1px solid #999',
      toggleButtonColor: '#fff'
    },
    navbarSettings: {
      horizontalAlign: 'start',
      showLabels: true
    },
    sidebarSettings: {
      sidebarToggleTrigger: 'hover',
      isSidebarExpanded: false,
      isSidebarExpandable: true,
      autoCollapse: false
    },
    mobileNavSettings: {
      icon: 'menu',
      iconSize: '3x',
      iconColor: '#fbfbfb',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      direction: 'X',
      width: '100%',
      hasOverlay: false,
      hideCloseIcon: false,
      closeIcon: 'close',
      closeIconSize: '2x',
      closeIconColor: '#fbfbfb'
    }
  }

  constructor (props) {
    super(props)
    this.state = {
      selectedTab: 1,
      isMobileMenuOpened: false
    }
    this.onChangeRoute = this.onChangeRoute.bind(this)
    this.toggleSidebar = this.toggleSidebar.bind(this)

    const { items, location } = props
    let routes = (items && Array.isArray(items) && items.filter(r => !r.hideInMenu)) || []
    const objectRoute = location && location.pathname && location.pathname.split('/')[1]
    const noTabSelected = items && !items.some(i => i.route && i.route.split('/')[1] === objectRoute)
    const firstTab = routes && routes[0] && routes[0].route
    if (noTabSelected && props.history && props.history.push) {
      props.history.replace(firstTab)
    }
  }

  componentDidMount () {
    this.setState({
      showLabel: this.props.sidebarSettings.isSidebarExpanded,
      sidebarExpanded: this.props.sidebarSettings.isSidebarExpanded
    })
    this.props.toggleShrink && this.props.toggleShrink(this.props.sidebarSettings.isSidebarExpanded)
  }

  componentDidUpdate (prevProps, prevState, snapshot) {
    const { sidebarSettings: { autoCollapse } = {}, location, navLayout, useAsMobileNav } = this.props
    const isSidebar = navLayout === 'sidebar'
    if (isSidebar && autoCollapse && prevProps.location?.key !== location?.key) {
      this.toggleSidebar(false)
    }
    if (useAsMobileNav && prevProps.location?.key !== location?.key) {
      this.toggleMobileMenu(false)
    }
  }

  isActive = (index, match) => {
    if (match && this.state.selectedTab !== index) {
      setTimeout(this.setState.bind(this, { selectedTab: index }), 0)
    }
  }

  onChangeRoute (selectedTab) {
    this.setState({
      selectedTab
    })
  }

  toggleSidebar (condition) {
    const state = arguments.length ? condition : !this.state.sidebarExpanded
    this.setState({
      sidebarExpanded: state
    }, () => {
      if (this.state.sidebarExpanded) {
        this.timeout = setTimeout(() => {
          this.setState({
            showLabel: true
          })
        }, 100)
      } else {
        !!this.timeout && clearTimeout(this.timeout)
        this.setState({
          showLabel: false
        })
      }
    })
    this.props.toggleShrink && this.props.toggleShrink(state)
  }

  toggleMobileMenu = (condition) => {
    this.setState({
      isMobileMenuOpened: condition
    })
  }

  render () {
    const {
      items,
      routes: navRoutes,
      location,
      navLayout,
      labelPosition,
      showIcons,
      useAsMobileNav,
      sidebarSettings: {
        sidebarToggleTrigger,
        isSidebarExpandable,
        isSidebarExpanded,
        triggerIcon
      } = {},
      navbarSettings: {
        horizontalAlign,
        showLabels
      } = {},
      mobileNavSettings: {
        icon = 'menu',
        iconSize = '2x',
        iconColor = '#fbfbfb',
        top = 0,
        left = 0,
        bottom = 0,
        right = 0,
        width = '100%',
        hasOverlay,
        hideCloseIcon,
        direction = 'X',
        closeIcon = 'close',
        closeIconSize = '2x',
        closeIconColor = '#fbfbfb'
      } = {},
      styling: {
        navBackground,
        navColor,
        navActiveColor,
        navHoverColor,
        navActiveIndicatorColor,
        shadow,
        rightBorder,
        toggleButtonColor
      } = {}
    } = this.props
    const { selectedTab, sidebarExpanded, showLabel, isMobileMenuOpened } = this.state
    const itemRoutes = items && !!items.length ? items : navRoutes
    let routes = (itemRoutes && itemRoutes.filter(r => !r.hideInMenu)) || []
    const isVerticalNav = navLayout === 'sidebar'
    return (
      <Fragment>
        {
          useAsMobileNav
            ? <div className={'h-full mobile-menu'}>
              <div className={'h-full flex items-center mobile-open-button'}>
                <IconComponent pointer icon={icon} iconSize={iconSize} styling={{ iconColor: iconColor }} onClick={() => this.toggleMobileMenu(!isMobileMenuOpened)} />
              </div>
              <ReactModal
                overlayClassName={'cc-overlay'}
                className={'mobile-menu'}
                ariaHideApp={false}
                closeTimeoutMS={300}
                isOpen={isMobileMenuOpened}
                style={{
                  overlay: {
                    zIndex: 2000,
                    backgroundColor: hasOverlay ? 'rgba(255, 255, 255, 0.75)' : 'transparent',
                    top,
                    left,
                    right,
                    bottom,
                  },
                  content: {
                    top,
                    left,
                    right,
                    bottom,
                    width,
                    background: navBackground || '#0000ff',
                    padding: '.5rem'
                  }
                }}
              >
                <div className={'w-full flex justify-end'}>
                  {
                    !hideCloseIcon &&
                    <div className={`mobile-close-button`}>
                      <IconComponent icon={closeIcon} iconSize={closeIconSize} styling={{ iconColor: closeIconColor }} onClick={() => this.toggleMobileMenu(false)} />
                    </div>
                  }
                </div>
                <ul>
                  {
                    routes.map((item, index) => {
                      const { href, route, icon, label, externalRoute } = item
                      const { pathname } = !!this.props.location && this.props.location || { pathname: ''}
                      const isActive = item.matchesPath ? item.matchesPath(pathname) : matchPath(pathname, { path: item.route })
                      return externalRoute
                        ? (
                          <li key={index + 1}>
                            <a
                              {...this.props}
                              className={`nav-link ${isActive ? `active` : ``}`}
                              href={href || '#'}
                              target='_blank'
                              style={{
                                display: `flex`,
                                flexDirection: labelPosition === `top` || labelPosition === `bottom`
                                  ? labelPosition === `top` ? `column-reverse` : `column`
                                  : labelPosition === `left` ? `row-reverse` : `row`
                              }}
                            >
                              {
                                showIcons && icon &&
                                <IconComponent icon={icon} iconSize={`2x`} />
                              }
                              <span>{label}</span>
                            </a>
                          </li>
                        )
                        : (
                          <li key={index + 1} className={`${isActive ? `active` : ``}`}>
                            <NavLink
                              className={`nav-link`}
                              location={location}
                              to={(route || label || '').replace(/\/$/, '')}
                              style={{
                                display: `flex`,
                                flexDirection: labelPosition === `top` || labelPosition === `bottom`
                                  ? labelPosition === `top` ? `column-reverse` : `column`
                                  : labelPosition === `left` ? `row-reverse` : `row`
                              }}
                            >
                              {
                                showIcons && icon &&
                                <IconComponent icon={icon} iconSize={`2x`} />
                              }
                              <span>{label}</span>
                            </NavLink>
                          </li>
                        )
                    })
                  }
                </ul>
              </ReactModal>
            </div>
            : <>
              <nav
                className={(((isVerticalNav && sidebarExpanded) || (isSidebarExpanded && !isSidebarExpandable)) && `expanded`) || ``}
                onMouseEnter={isSidebarExpandable && !sidebarExpanded && isVerticalNav && sidebarToggleTrigger === `hover` ? this.toggleSidebar : undefined}
                onMouseLeave={isSidebarExpandable && sidebarExpanded && isVerticalNav && sidebarToggleTrigger === `hover` ? this.toggleSidebar : undefined}
              >
                <ul>
                  <TabIndicator defaultActiveKey={selectedTab} selectedTab={selectedTab} onChangeTab={this.onChangeRoute}>
                    {
                      routes.map((item, index) => {
                        const { href, route, icon, label, externalRoute } = item
                        const { pathname } = !!this.props.location && this.props.location
                        const isActive = item.matchesPath ? item.matchesPath(pathname) : matchPath(pathname, { path: item.route })
                        return externalRoute
                          ? (
                            <li key={index + 1}>
                              <a
                                {...this.props}
                                className={`nav-link ${isActive ? `active` : ``}`}
                                href={href || '#'}
                                target='_blank'
                                style={{
                                  display: `flex`,
                                  flexDirection: labelPosition === `top` || labelPosition === `bottom`
                                    ? labelPosition === `top` ? `column-reverse` : `column`
                                    : labelPosition === `left` ? `row-reverse` : `row`
                                }}
                              >
                                {
                                  showIcons && icon &&
                                  <IconComponent icon={icon} iconSize={`2x`} />
                                }
                                {
                                  ((isVerticalNav && !isSidebarExpandable && isSidebarExpanded) || (isVerticalNav && showLabel) || (!isVerticalNav && showLabels)) &&
                                  <span>{label}</span>
                                }
                              </a>
                            </li>
                          )
                          : (
                            <li key={index + 1} className={`${isActive ? `active` : ``}`}>
                              <NavLink
                                className={`nav-link`}
                                location={location}
                                to={(route || label || '').replace(/\/$/, '')}
                                style={{
                                  display: `flex`,
                                  flexDirection: labelPosition === `top` || labelPosition === `bottom`
                                    ? labelPosition === `top` ? `column-reverse` : `column`
                                    : labelPosition === `left` ? `row-reverse` : `row`
                                }}
                              >
                                {
                                  showIcons && icon &&
                                  <IconComponent icon={icon} iconSize={`2x`} />
                                }
                                {
                                  ((isVerticalNav && isSidebarExpanded && !isSidebarExpandable) || (isVerticalNav && showLabel) || (!isVerticalNav && showLabels)) &&
                                  <span>{label}</span>
                                }
                              </NavLink>
                            </li>
                          )
                      })
                    }
                  </TabIndicator>
                </ul>
                {isVerticalNav && sidebarToggleTrigger === `divider` && <div className={`vertical-divider`} onClick={this.toggleSidebar} />}
                {
                  isSidebarExpandable && isVerticalNav && sidebarToggleTrigger === `button` &&
                  <div className={`toggle-button`}>
                    <IconComponent icon={triggerIcon} onClick={isVerticalNav ? this.toggleSidebar : undefined} />
                  </div>
                }
              </nav>
            </>
        }
        <style jsx>{`
          nav {
            display: flex;
            align-items: ${isVerticalNav ? `flex-start` : `center`};
            z-index: 1998;
            overflow: hidden;
            min-width: ${isVerticalNav ? `64px` : `100%`};
            min-height: 50px;
            height: ${isVerticalNav ? `100vh` : `100%`};
            -webkit-transition: min-width 0.25s ease-out;
            -moz-transition: min-width 0.25s ease-out;
            -o-transition: min-width 0.25s ease-out;
            -ms-transition: min-width 0.25s ease-out;
            transition: min-width 0.25s ease-out;
            background: ${navBackground};
            -webkit-box-shadow: ${shadow};
            -moz-box-shadow: ${shadow};
            box-shadow: ${shadow};
            border-right: ${isVerticalNav ? rightBorder : `none`};
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
            padding-top: ${(isVerticalNav && sidebarToggleTrigger === `button` && `2rem`) || 0};
          }
          nav.expanded {
            min-width: 240px;
          }
          .vertical-divider {
            width: 10px;
            height: 100%;
            background: transparent;
            cursor: col-resize
          }
          ul {
            margin: 0;
            padding: ${isVerticalNav ? `1rem 0.1rem` : `0 1rem 0.1rem`};
            list-style: none;
            display: flex;
            flex-direction: ${isVerticalNav ? `column` : `row`};
            width: 100%;
            justify-content: ${horizontalAlign === `center` ? `center` : `flex-${horizontalAlign}`};
          }

          .toggle-button {
            transform: rotate(${sidebarExpanded ? `180deg` : `360deg`});
            position: absolute;
            top: 0;
            right: 0;
            transition: transform 0.25s;
            cursor: pointer;
            color: ${toggleButtonColor}
          }

          li {
            text-align: ${isVerticalNav ? (labelPosition === `left` && `right`) || `left` : `center`};
          }

          li :global(.nav-link) {
            color: ${navColor} !important;
          }

          li :global(.zmdi) {
            margin: ${isVerticalNav ? 0 : `0 auto`};
          }

          li .nav-link:hover {
            text-decoration: none !important;
          }

          li span {
            margin: auto 0;
          }

          li:hover :global(.nav-link) {
            color: ${navHoverColor} !important;
          }

          li > :global(.nav-link.active) {
            border-bottom: ${isVerticalNav ? `none` : `4px solid ${navActiveIndicatorColor}`};
            border-right: ${isVerticalNav ? `4px solid ${navActiveIndicatorColor}` : `none`};
            color: ${navActiveColor} !important;
          }
          
          :global(.mobile-menu li > .nav-link.active) {
            border-right: none;
            border-left: 4px solid ${navActiveIndicatorColor};
          }
          
          :global(.cc-overlay.ReactModal__Overlay) {
            opacity: 0;
            transform: translate${direction}(-1100px);
            transition: all 300ms ease-in-out;
          }
          
          :global(.cc-overlay.ReactModal__Overlay--after-open) {
            opacity: 1;
            transform: translate${direction}(0px);
          }
          
          :global(.cc-overlay.ReactModal__Overlay--before-close) {
            opacity: 0;
            transform: translate${direction}(-1100px);
          }
        `}</style>
      </Fragment>
    )
  }
}

export default Navigation
